<template>
	<div class="total-container">
		<div class="total-row">
			<span class="total-label">Subtotal</span>
			<span class="total-amount">{{ formattedSubtotal }}</span>
		</div>
		<div class="total-row">
			<div class="vat-label-group">
				<span class="total-label">VAT</span>
				<button
					type="button"
					class="vat-swap"
					role="switch"
					:aria-checked="vatEnabled"
					:disabled="disabled"
					@click="handleToggleVat"
				>
					<svg class="vat-swap-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
						<path
							d="M4 8h12.5l-2.8-2.8a1 1 0 1 1 1.4-1.4l4.5 4.5a1 1 0 0 1 0 1.4l-4.5 4.5a1 1 0 0 1-1.4-1.4L16.5 10H4a1 1 0 1 1 0-2Zm16 8H7.5l2.8 2.8a1 1 0 1 1-1.4 1.4l-4.5-4.5a1 1 0 0 1 0-1.4l4.5-4.5a1 1 0 0 1 1.4 1.4L7.5 14H20a1 1 0 1 1 0 2Z"
						/>
					</svg>
					<span class="vat-active-rate">{{ vatEnabled ? '20%' : '0%' }}</span>
				</button>
			</div>
			<span class="total-amount">{{ formattedVat }}</span>
		</div>
		<div class="total-row total-final">
			<span class="total-label">Total</span>
			<span class="total-amount">{{ formattedTotal }}</span>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';
import { INVOICE_CURRENCY } from '@/constants/invoiceDefaults';

const props = defineProps({
	subtotalAmount: {
		type: [String, Number],
		default: '0.00',
	},
	vatAmount: {
		type: [String, Number],
		default: '0.00',
	},
	totalAmount: {
		type: [String, Number],
		default: '0.00',
	},
	vatRate: {
		type: Number,
		default: 0.2,
	},
	vatEnabled: {
		type: Boolean,
		default: true,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['update:vatEnabled']);

const currencyFormatter = new Intl.NumberFormat('en-GB', {
	style: 'currency',
	currency: INVOICE_CURRENCY,
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

const formatAmount = (value) => {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return currencyFormatter.format(0);
	const absolute = currencyFormatter.format(Math.abs(numeric));
	return numeric < 0 ? `-${absolute}` : absolute;
};

const formattedSubtotal = computed(() => formatAmount(props.subtotalAmount));
const formattedVat = computed(() => formatAmount(props.vatAmount));
const formattedTotal = computed(() => formatAmount(props.totalAmount));

const handleToggleVat = () => {
	if (props.disabled) return;
	emit('update:vatEnabled', !props.vatEnabled);
};
</script>

<style scoped>
.total-container {
	display: flex;
	flex-direction: column;
	align-self: flex-start;
	gap: 0;
	width: 100%;
	border: 1px solid var(--border-color);
	border-radius: 0;
	box-sizing: border-box;
}

.total-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 24px; /* spacing-large */
	padding: 8px 16px; /* spacing-small spacing-medium */
	background-color: var(--noc-background-secondary);
	border: 0;
	border-radius: 0;
	min-width: 0;
}

.total-row + .total-row {
	border-top: 1px solid var(--border-color);
}

.vat-label-group {
	display: inline-flex;
	align-items: center;
	gap: 10px;
}

.vat-swap {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 3px 6px;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	background: var(--noc-background, #fff);
	color: var(--text-color-secondary);
	font-size: 0.85rem;
	font-weight: 600;
	cursor: pointer;
	transition: border-color 0.12s ease;
}

.vat-swap:hover:not(:disabled) {
	border-color: var(--border-color-active);
}

.vat-swap:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.vat-swap-icon {
	width: 0.85rem;
	height: 0.85rem;
	fill: currentColor;
}

.vat-active-rate {
	color: var(--noc-blue);
	font-size: 0.85rem;
	font-weight: 700;
	line-height: 1;
	min-width: 26px;
	text-align: right;
}

.total-label {
	color: var(--text-color);
	font-size: 0.9rem;
	font-weight: 500;
}

.total-amount {
	color: var(--text-color);
	font-size: 0.9rem;
	font-weight: 600;
	text-align: right;
}

.total-row.total-final {
	background-color: var(--noc-blue-05);
	font-weight: 600;
}

.total-row.total-final .total-label {
	font-size: 1rem;
	font-weight: 600;
}

.total-row.total-final .total-amount {
	color: var(--noc-blue);
	font-size: 1.1rem;
	font-weight: 700;
}
</style>
